import { DemarrageProgressBar } from '@teetsh/app/src/containers/Demarrage/components/DemarrageProgressBar';
import React from 'react';
import { ButtonNavLink } from '@teetsh/app/src/components/Buttons/NavLink';
import tw from 'twin.macro';
import { computeNextStep } from '@teetsh/app/src/containers/Demarrage/steps/computeNextStep';
import { DemarrageStep } from '@teetsh/app/src/containers/Demarrage/types';
import { useDemarrage } from '@teetsh/app/src/containers/Demarrage/queries/useDemarrage';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { useTranslation } from 'react-i18next';

interface Props {
  children?: any;
  title: React.ReactNode;
  subtitle?: any;
  currentStep: DemarrageStep;
  nextDisabled?: boolean;
  nextLabel?: string;
  nextIcon?: any;
  hideFooter?: boolean;
  centerVertically?: boolean;
  centerTitle?: boolean;
  presentationSlice?: boolean;
  header?: React.ReactNode;
  onNext?: () => Promise<void> | void;
}

export function StepWrapper(props: Props) {
  const {
    title,
    subtitle,
    children,
    nextDisabled,
    nextLabel,
    hideFooter,
    centerVertically,
    centerTitle,
    presentationSlice,
    header,
  } = props;
  const navigate = useNavigate();
  const { search } = useLocation();
  const user = useUser();
  const { t } = useTranslation(['common']);

  const urlSearchParams = new URLSearchParams(search);
  const isAddTeacherMode = urlSearchParams.get('mode') === 'add-teacher-role';

  const { data: demarrageData, isLoading } = useDemarrage();

  const { nextStep, previousStep, progression } =
    isLoading || !demarrageData
      ? { nextStep: undefined, previousStep: undefined, progression: undefined }
      : computeNextStep(
          props.currentStep,
          demarrageData,
          demarrageData.userCountryCode || user.country || 'FR',
          isAddTeacherMode,
        );

  if (isLoading || !demarrageData) {
    return null;
  }

  return (
    <div tw="h-full flex flex-col md:rounded-lg bg-neutral-supershy">
      {/* CONTENT */}
      <div
        css={[
          tw`p-6 md:px-8 grow overflow-y-auto`,
          centerVertically && tw`flex flex-col items-center justify-center`,
        ]}
      >
        {header}
        {progression && (
          <DemarrageProgressBar
            value={progression}
            currentStep={props.currentStep}
          />
        )}
        <h1
          css={[
            tw`my-6 text-4xl display leading-none`,
            centerTitle && tw`text-center`,
          ]}
        >
          {title}
        </h1>
        {subtitle && (
          <div css={[tw`text-lg mb-8`, presentationSlice && tw`text-center`]}>
            {subtitle}
          </div>
        )}
        {children}
      </div>
      {/* FOOTER */}
      {!hideFooter && (
        <div
          css={[
            tw`px-6 py-4 space-x-2.5`,
            presentationSlice
              ? tw`text-center pb-6 pt-0`
              : tw`text-right border-t`,
          ]}
        >
          {previousStep && (
            <ButtonNavLink
              large
              outlined
              to={`/demarrage/${previousStep}${search}`}
              intent="none"
            >
              {t('common:buttons.previous')}
            </ButtonNavLink>
          )}
          {/* C'est un bouton car disable un link ça n'a pas vraiment de sens, et pour les tests ça devient galère*/}
          <Button
            disabled={nextDisabled}
            onClick={async () => {
              if (props.onNext) {
                await props.onNext();
              }
              navigate(`/demarrage/${nextStep}${search}`, { replace: true });
            }}
            intent="primary"
            large
            rightIcon={props.nextIcon}
            data-testid="next-button"
          >
            {nextLabel || t('common:buttons.next')}
          </Button>
        </div>
      )}
    </div>
  );
}
